"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Droplets, Wind, ArrowRight, CircleCheck as CheckCircle2, FileText, PhoneCall } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyBenefits = [{
  title: "Supressão e captura no ponto de emissão",
  description: "Gotículas calibradas para agregar e precipitar poeiras suspensas em transportadores e tremonhas."
}, {
  title: "Poupança de água e energia",
  description: "Sistemas de controlo que aplicam apenas o caudal estritamente necessário."
}, {
  title: "Representação Spraying Systems Co.",
  description: "Tecnologia de ponta e fiabilidade comprovada em Portugal desde 1993."
}];
const technicalMetrics = [{
  value: "95%+",
  label: "Eficiência de retenção de partículas"
}, {
  value: "-60%",
  label: "Redução no consumo de água"
}, {
  value: "30+ Anos",
  label: "Experiência industrial em Portugal"
}];
export default function DustControlHero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="dustcontrolhero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 border-b border-border">
      {/* Background Accent Gradients */}
      <div className="absolute top-0 right-0 -z-10 h-[500px] w-[500px] rounded-full bg-secondary/10 blur-3xl" />
      <div className="absolute bottom-0 left-0 -z-10 h-[400px] w-[400px] rounded-full bg-primary/5 blur-3xl" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Copy Column */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-7 flex flex-col items-start text-left space-y-6">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="border-secondary text-secondary bg-secondary/10 gap-1.5 px-3 py-1 font-medium text-xs">
                <Wind className="w-3.5 h-3.5 text-secondary" />
                <span>Aplicações Industriais</span>
              </Badge>
              <Badge variant="secondary" className="bg-primary text-primary-foreground text-xs px-3 py-1">
                Spraying Systems Co.
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Controlo de poeiras eficiente e sustentável
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">Controle as poeiras em suspensão, melhore a segurança e reduza a perda de material com tecnologia de pulverização concebida para ambientes industriais exigentes. A Spraying Systems Co. ajuda as instalações a aplicar o tamanho de gota, o padrão de pulverização e o design de sistema adequados para suprimir poeiras em pontos de transferência, pilhas de armazenamento, trituradores, correias transportadoras e outras áreas de processo geradoras de poeiras.</motion.p>

            {/* Quick Benefits Bullet List */}
            <motion.div variants={itemVariants} className="w-full pt-2 space-y-3">
              {keyBenefits.map((benefit, i) => <div key={benefit.title} data-index={i} className="flex items-start gap-3">
                  <div className="mt-1 rounded-full bg-accent text-accent-foreground p-1 shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-secondary" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-foreground">
                      {benefit.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      {benefit.description}
                    </p>
                  </div>
                </div>)}
            </motion.div>

            {/* Actions */}
            <motion.div variants={itemVariants} className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md">
                <Link to="/ContactsPage" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="w-4 h-4" />
                  <span>Consulte-nos</span>
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent">
                <Link to="/CatalogsPage" className="inline-flex items-center justify-center gap-2">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  <span>Catálogo técnico</span>
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Media / Technical Card Column */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          ease: "easeOut",
          delay: 0.2
        }} className="lg:col-span-5">
            <Card className="border-border shadow-lg bg-card overflow-hidden">
              <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/dust_control_system_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1738660/img/dust_control_system_hero.png-wvc-sizes" alt="Sistema industrial de controlo e supressão de poeiras" loading="lazy" decoding="async" className="h-full w-full object-cover" src="https://eurocontrol.pt/wp-content/uploads/2026/09/Captura-de-ecra-2026-09-07-as-15.40.04.png" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent flex items-end p-6">
                  <div className="text-white space-y-1">
                    <span className="text-xs uppercase tracking-wider font-semibold text-secondary-foreground bg-secondary/80 px-2 py-0.5 rounded-sm">
                      Tecnologia de Atomização
                    </span>
                    <h3 className="text-lg font-bold text-white leading-tight">
                      Supressão ativa de partículas PM10 e PM2.5
                    </h3>
                  </div>
                </div>
              </div>

              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-3 gap-4 text-center">
                  {technicalMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col items-center">
                      <span className="text-xl sm:text-2xl font-extrabold text-primary">
                        {metric.value}
                      </span>
                      <span className="text-xs text-muted-foreground font-medium mt-1 leading-tight">
                        {metric.label}
                      </span>
                    </div>)}
                </div>

                <Separator />

                <div className="flex items-center justify-between gap-4 pt-1">
                  <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                      <Droplets className="w-5 h-5 text-secondary" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground">Engenharia Eurocontrol</p>
                      <p className="text-xs text-muted-foreground">Dimensionamento à medida</p>
                    </div>
                  </div>

                  <Button asChild variant="ghost" size="sm" className="text-secondary hover:text-secondary-foreground hover:bg-secondary">
                    <Link to="/ApplicationsPage" className="text-xs font-semibold inline-flex items-center gap-1">
                      <span>Ver aplicações</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}